import styles from "@/styles/CatalogueRequestCell.module.css";
import { Other, Tent, Darts } from "./Database";
import { Inter } from "next/font/google";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const inter = Inter({ subsets: ["latin"] });

type CatalogueRequestCellProps = {
  type: "other" | "tent" | "darts";
  content: Other | Tent | Darts;
};

export default function CatalogueRequestCell({ type, content }: CatalogueRequestCellProps) {
  const router = useRouter();
  const nl = router.asPath.includes("/nl/");
  const fr = router.asPath.includes("/fr/");

  const amountRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [requests, setRequests] = useState<string[] | null>([]);
  const [requestsUnformatted, setRequestsUnformatted] = useState<string | null>(null);
  const [amount, setAmount] = useState<string | null>(null);

  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const images = [content.imagePath];

  useEffect(() => {
    const intervalId = setInterval(() => {
      const newRequestsUnformatted = sessionStorage.getItem("Requests");
      const newAmount = sessionStorage.getItem(content.nameNL);
      if (newRequestsUnformatted !== requestsUnformatted) {
        setRequestsUnformatted(newRequestsUnformatted);
        setRequests(newRequestsUnformatted ? newRequestsUnformatted.split(",").concat() : null);
      }
      if (newAmount !== amount) {
        setAmount(newAmount);
        if (amountRef.current) {
          amountRef.current.value = newAmount || "1";
        }
      }
    }, 100);
    return () => clearInterval(intervalId);
  }, [requests, amount, requestsUnformatted, content.nameNL]);

  useEffect(() => {
    if (isViewerOpen) {
      document.body.style.overflow = "hidden"; // Disable scrolling
    } else {
      document.body.style.overflow = ""; // Enable scrolling
    }
    return () => {
      document.body.style.overflow = ""; // Re-enable scrolling when component unmounts
    };
  }, [isViewerOpen]);

  // Check clicked outside, close
  useEffect(() => {
    const handleOutSideClick = (event: Event) => {
      if (event.target != null && !imageRef.current?.contains(event.target as Node)) {
        setIsViewerOpen(false);
      }
    };
    window.addEventListener("mousedown", handleOutSideClick);
    return () => {
      window.removeEventListener("mousedown", handleOutSideClick);
    };
  }, [imageRef]);

  const openImageViewer = useCallback((index: number) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  function requestButtonText() {
    if (nl) {
      return <>Aanvragen</>;
    } else if (fr) {
      return <>Demander</>;
    }
  }

  function requestClicked() {
    if (requests && requests.includes(content.nameNL)) {
      const resultingRequests = requests.filter((request) => request !== content.nameNL).join(",");
      if (resultingRequests === "") {
        sessionStorage.removeItem("Requests");
      } else {
        sessionStorage.setItem("Requests", resultingRequests);
      }
      sessionStorage.removeItem(content.nameNL);
    } else {
      sessionStorage.setItem(
        "Requests",
        requests ? requests.concat(content.nameNL).join(",") : content.nameNL
      );
      sessionStorage.setItem(content.nameNL, amountRef.current?.value || "1");
    }
  }

  function amountChanged(event: React.ChangeEvent<HTMLInputElement>) {
    if (sessionStorage.getItem(content.nameNL) !== event.target.value && event.target.value !== "") {
      sessionStorage.setItem(content.nameNL, event.target.value);
    }
  }

  function showRequestButton() {
    if (requests && requests.includes(content.nameNL)) {
      return (
        <button
          className={styles.requestButton}
          onClick={() => requestClicked()}
          style={{ backgroundColor: "#4D7EA8", color: "white" }}
        >
          {requestButtonText()}
        </button>
      );
    } else {
      return (
        <button className={styles.requestButton} style={{ backgroundColor: "#f0f0f0", color: "black" }} onClick={() => requestClicked()}>
          {requestButtonText()}
        </button>
      );
    }
  }

  function showDimensions() {
    content = content as Tent;
    if (content.width && content.depth && content.height) {
      if (nl) {
        return (
          <p className={styles.dimensions}>
            B: {content.width}m &ndash; D: {content.depth}m &ndash; H: {content.height}m
          </p>
        );
      } else if (fr) {
        return (
          <p className={styles.dimensions}>
            L: {content.width}m &ndash; P: {content.depth}m &ndash; H: {content.height}m
          </p>
        );
      }
    } else return null;
  }

  if (type === "other") {
    return (
      <div id={content.nameNL} className={styles.catalogueCell}>
        {nl && (
          <>
            {content.imagePath ? (
              <>
                <img
                  src={content.imagePath}
                  alt="Foto van een spel"
                  width={300}
                  height={300}
                  onClick={() => openImageViewer(0)}
                />
                {isViewerOpen && (
                  <div className={styles.imageViewer}>
                    <div className={styles.wrapper}>
                      <button className={styles.closeButton} onClick={closeImageViewer}>
                        <X size={40} />
                      </button>
                      <div className={styles.content}>
                        <img
                          src={images[currentImage]}
                          alt="Photo d'un château gonflable"
                          width={300}
                          height={300}
                          style={{ maxWidth: "none", maxHeight: "none" }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: "300px", height: "300px" }}></div>
            )}
            <p className={styles.name}>{content.nameNL}</p>
            <p className={styles.price}>Prijs op aanvraag</p>
          </>
        )}
        {fr && (
          <>
            {content.imagePath ? (
              <>
                <img
                  src={content.imagePath}
                  alt="Photo d'un jeu"
                  width={300}
                  height={300}
                  onClick={() => openImageViewer(0)}
                />
                {isViewerOpen && (
                  <div className={styles.imageViewer}>
                    <div className={styles.wrapper}>
                      <button className={styles.closeButton} onClick={closeImageViewer}>
                        <X size={40} />
                      </button>
                      <div className={styles.content}>
                        <img
                          src={images[currentImage]}
                          alt="Photo d'un château gonflable"
                          width={300}
                          height={300}
                          style={{ maxWidth: "none", maxHeight: "none" }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: "300px", height: "300px" }}></div>
            )}
            <p className={styles.name}>{content.nameFR}</p>
            <p className={styles.price}>Prix sur demande</p>
          </>
        )}
        <div className={styles.buttons}>{showRequestButton()}</div>
      </div>
    );
  } else if (type === "tent") {
    return (
      <div id={content.nameNL} className={styles.catalogueCell}>
        {nl && (
          <>
            {content.imagePath ? (
              <>
                <img
                  src={content.imagePath}
                  alt="Foto van tent of tent-toebehoren"
                  width={300}
                  height={300}
                  onClick={() => openImageViewer(0)}
                />
                {isViewerOpen && (
                  <div className={styles.imageViewer}>
                    <div className={styles.wrapper}>
                      <button className={styles.closeButton} onClick={closeImageViewer}>
                        <X size={40} />
                      </button>
                      <div className={styles.content}>
                        <img
                          src={images[currentImage]}
                          alt="Photo d'un château gonflable"
                          width={300}
                          height={300}
                          style={{ maxWidth: "none", maxHeight: "none" }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: "300px", height: "300px" }}></div>
            )}
            <p className={styles.name}>{content.nameNL}</p>
            {showDimensions()}
            <p className={styles.price}>&euro;{(content as Tent).price} / weekend</p>
            <input
              id={`amountInput-${content.imagePath}`}
              className={styles.amountInput}
              type="number"
              placeholder="Aantal"
              defaultValue={amount || "1"}
              min="1"
              ref={amountRef}
              onChange={amountChanged}
            />
          </>
        )}
        {fr && (
          <>
            {content.imagePath ? (
              <>
                <img
                  src={content.imagePath}
                  alt="Photo d'une tente ou d'accessoires de tente"
                  width={300}
                  height={300}
                  onClick={() => openImageViewer(0)}
                />
                {isViewerOpen && (
                  <div className={styles.imageViewer}>
                    <div className={styles.wrapper}>
                      <button className={styles.closeButton} onClick={closeImageViewer}>
                        <X size={40} />
                      </button>
                      <div className={styles.content}>
                        <img
                          src={images[currentImage]}
                          alt="Photo d'un château gonflable"
                          width={300}
                          height={300}
                          style={{ maxWidth: "none", maxHeight: "none" }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: "300px", height: "300px" }}></div>
            )}
            <p className={styles.name}>{content.nameFR}</p>
            {showDimensions()}
            <p className={styles.price}>&euro;{(content as Tent).price} / weekend</p>
            <input
              id={`amountInput-${content.imagePath}`}
              className={styles.amountInput}
              type="number"
              placeholder="Aantal"
              defaultValue={amount || "1"}
              min="1"
              ref={amountRef}
              onChange={amountChanged}
            />
          </>
        )}
        <div className={styles.buttons}>{showRequestButton()}</div>
      </div>
    );
  } else if (type === "darts") {
    return (
      <div id={content.nameNL} className={styles.catalogueCell}>
        {nl && (
          <>
            {content.imagePath ? (
              <>
                <img
                  src={content.imagePath}
                  alt="Foto van een dartstand of dartstand-toebehoren"
                  width={300}
                  height={300}
                  onClick={() => openImageViewer(0)}
                />
                {isViewerOpen && (
                  <div className={styles.imageViewer}>
                    <div className={styles.wrapper}>
                      <button className={styles.closeButton} onClick={closeImageViewer}>
                        <X size={40} />
                      </button>
                      <div className={styles.content}>
                        <img
                          src={images[currentImage]}
                          alt="Foto van een dartstand of dartstand-toebehoren"
                          width={300}
                          height={300}
                          style={{ maxWidth: "none", maxHeight: "none" }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: "300px", height: "300px" }}></div>
            )}
            <p className={styles.name}>{content.nameNL}</p>
            {showDimensions()}
            <p className={styles.price}>&euro;{(content as Tent).price} / dag*</p>
            <input
              id={`amountInput-${content.imagePath}`}
              className={styles.amountInput}
              type="number"
              placeholder="Aantal"
              defaultValue={amount || "1"}
              min="1"
              ref={amountRef}
              onChange={amountChanged}
            />
          </>
        )}
        {fr && (
          <>
            {content.imagePath ? (
              <>
                <img
                  src={content.imagePath}
                  alt="Photo d'un support de fléchettes ou d'accessoires pour support de fléchettes"
                  width={300}
                  height={300}
                  onClick={() => openImageViewer(0)}
                />
                {isViewerOpen && (
                  <div className={styles.imageViewer}>
                    <div className={styles.wrapper}>
                      <button className={styles.closeButton} onClick={closeImageViewer}>
                        <X size={40} />
                      </button>
                      <div className={styles.content}>
                        <img
                          src={images[currentImage]}
                          alt="Photo d'un support de fléchettes ou d'accessoires pour support de fléchettes"
                          width={300}
                          height={300}
                          style={{ maxWidth: "none", maxHeight: "none" }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ width: "300px", height: "300px" }}></div>
            )}
            <p className={styles.name}>{content.nameFR}</p>
            {showDimensions()}
            <p className={styles.price}>&euro;{(content as Tent).price} / jour</p>
            <input
              id={`amountInput-${content.imagePath}`}
              className={styles.amountInput}
              type="number"
              placeholder="Aantal"
              defaultValue={amount || "1"}
              min="1"
              ref={amountRef}
              onChange={amountChanged}
            />
          </>
        )}
        <div className={styles.buttons}>{showRequestButton()}</div>
      </div>
    );
  }
}
